/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useCallback, useEffect, useState } from "react";
import { Box } from "native-base";

import {
    AuthPayload,
    useAppSettings,
    useAuthPin,
    useCheckAuth,
    useRequestPin,
    useSpringBigStore
} from "@springbig/shared";

import PinForm, { PinFormData } from "./components/PinForm";
import { Caption, H3 } from "~/shared/Typography";
import Section from "~/shared/Section";
import { useAuthStyles, useGeneralStyles } from "~/shared/styles";
import { useAppNavigate } from "..";
import { useLocation } from "react-router";
import Button from "~/shared/Button";
import Pressable from '~/shared/Pressable';


const SharedPinScreen = () => {
    const [error, setError] = useState<string | null>(null);
    const [stashboardOrMemberKey, setStashboardOrMemberKey] = useState<
        AuthPayload["reference"]
    >();
    const [disabled, setDisabled] = useState(false);

    const { mobileSubscriberId, walletId, cleanState, setWalletId, member } = useSpringBigStore();

    const { mutate: checkAuth } = useCheckAuth();
    const { mutate: authPin } = useAuthPin();
    const { mutate: requestPin, error: requestPinError } = useRequestPin();

    const { text, icon } = useAuthStyles();
    const { navigate, params } = useAppNavigate();
    const location = useLocation();
    const gStyle = useGeneralStyles();
    const [merchantName, setMerchantName] = useState('');
    const [pinError, setPinError] = useState('');
    const { defaultScreen } = useAppSettings();


    const handleResendCode = () => {
        const _params =
            stashboardOrMemberKey === "stashboard"
                ? params.stashboard_key
                : params.wallet_id;

        _params && createRequestPin(_params!);
        setDisabled(true);
    };

    useEffect(() => {
        let timer;

        if (disabled) {
            timer = setTimeout(() => {
                setDisabled(false);
            }, 60 * 1000);

            return () => clearTimeout(timer);
        }
    }, [disabled]);

    const handleSubmit = useCallback(
        async (values: PinFormData) => {
            if (stashboardOrMemberKey) {
                const _params =
                    stashboardOrMemberKey === "stashboard"
                        ? params.stashboard_key
                        : params.wallet_id;

                authPin({
                    stashboardOrMemberKey: _params!,
                    pin: btoa(values.pin),
                    reference: stashboardOrMemberKey!
                }, {
                    onError: (error: any, response: any) => {
                        if (error.status === 401) {
                            setPinError("Must enter valid code")
                        }
                    }
                });

            }
        }, [stashboardOrMemberKey, params, authPin]
    );

    const getAuthMember = useCallback(
        async (id: string, reference: AuthPayload["reference"]) => {

            checkAuth({
                stashboardOrMemberKey: id,
                reference,
            }, {
                onError: (error: any, response: any) => {

                    if (error.status === 401) {
                        setWalletId(error.data.merchant_member_key);
                        setMerchantName(error.data.merchant_dba_name);
                        setPinError("")
                    }
                }
            });

        },
        []
    );

    const createRequestPin = useCallback(
        async (id: string) => {

            id && requestPin({
                stashboardOrMemberKey: id,
                reference: stashboardOrMemberKey!
            });
        },
        [stashboardOrMemberKey, requestPin]
    );

    useEffect(() => {
        document.title = 'Secure SMS Viewer | Log in';
        (document as any).description = "Access your messages"
    }, []);

    useEffect(() => {
        if (location?.state?.isStashboard && walletId) {
            navigate(`w/${walletId}`, { state: { from: location.pathname, isUnAuthorised: true, isStashboard: false } });
        } else if (location?.state?.isStashboard && params && params.wallet_id) {
            getAuthMember(params.wallet_id, "stashboard");
            setStashboardOrMemberKey("stashboard");
        } else if (params && params.wallet_id) {
            getAuthMember(params.wallet_id, "member");
            setStashboardOrMemberKey("member");
        } else if (params && params.stashboard_key) {
            getAuthMember(params.stashboard_key, "stashboard");
            setStashboardOrMemberKey("stashboard");
        }
    }, [getAuthMember, params.stashboard_key, params.wallet_id]);

    useEffect(
        () => {

            const plan_id = localStorage.getItem('plan_id');
            if (plan_id) {
                localStorage.removeItem('plan_id');
                navigate(`w/${params.wallet_id}/subscription/${plan_id}`)
            }

            const link_id = localStorage.getItem('link_id');
            if (link_id) {
                localStorage.removeItem('link_id');
                navigate(`w/${params.wallet_id}/subscriptions`)
            }

            if (member && mobileSubscriberId && params.wallet_id && walletId === params.wallet_id) {
                location.state ? window.location.assign(location.state.from) : navigate(`w/${params.wallet_id}/${(defaultScreen || 'messages').toLocaleLowerCase()}`);
            } else if (walletId && walletId !== params.wallet_id && params.wallet_id !== undefined) {
                return () => {

                    cleanState();
                };

            }

            if (mobileSubscriberId && params.stashboard_key && walletId) {
                navigate(`w/${walletId}/messages/${params.stashboard_key}`);
            }

        },
        [mobileSubscriberId, navigate, params, walletId, cleanState, member, location.state]
    );

    useEffect(
        () => {
            if (requestPinError) {

                navigate("404");
            }
        },
        [requestPinError, navigate]
    );

    const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            const height = window.innerHeight;
            setViewportHeight(height);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleLinkPress = (url: string): void => {
        if (!url) return;
        window.open(url, '_blank');
    }

    return (
        <Section
            w={'clamp(320px, 100vw, 480px)'}
            h={viewportHeight}
            bg={gStyle.screen_background}
            overflowY={'auto'}
            mx={'auto'}
            py={10}
        >
            <Box
                alignItems="center"
                display={'flex'}
                justifyContent={"center"}
            >
                <H3 color={text} mb={4} text={'auth.register.title'} textValues={[merchantName]} />
                <Caption
                    color={text}
                    w={"80%"}
                    mb={4}
                    textAlign="center"
                    text="auth.validation.shared_pin_paragraph"
                />
                <Box w="80%">
                    <PinForm
                        isLoading={false}
                        error={error || pinError}
                        handleSubmitValues={handleSubmit}
                    />
                </Box>
                <Caption
                    color={gStyle.text}
                    opacity={disabled ? 0.3 : 1}
                    fontWeight="bold"
                    w={"80%"}
                    mt={24}
                    textAlign="center"
                    text="auth.validation.forgot_code"
                />
                <Button
                    testID='refer-copy-link-btn'
                    bg={gStyle.button_background}
                    _text={{
                        color: gStyle.button_text
                    }}
                    minW={'40%'}
                    mt={2}
                    isDisabled={disabled}
                    onPress={() => handleResendCode()}
                >
                    {disabled ? 'sending code now' : 'Click here for code'}
                </Button>
                <Pressable onPress={() => handleLinkPress('https://my.securelinkit.com/terms_conditions')}>
                    <Caption
                        mt={10}
                        color={icon}
                        w={"100%"}
                        my={1}
                        textAlign="center"
                        text="general.terms_and_conditions"
                    />
                </Pressable>
                <Pressable onPress={() => handleLinkPress('https://my.securelinkit.com/privacy')}>
                    <Caption
                        color={icon}
                        w={"100%"}
                        my={1}
                        textAlign="center"
                        text="general.privacy_policy"
                    />
                </Pressable>

                <Caption textAlign={'center'} mt={50}>
                    Message frequency may vary. Message & Data Rates may apply. Want to unsubscribe? Reply <b>STOP</b> to your text message.
                    Need help? Reply <b>HELP</b> to your text message. No customer information is transmitted or shared through these text message notifications.
                    No mobile information will be shared with third parties for marketing purposes.
                </Caption>
            </Box>
        </Section>
    );
};

export default SharedPinScreen;
